/**
 * Phase 24 / Task 24.2 (DEC-092) — Catalog Sync Service tables.
 *
 * Platform-level config/log/queue for the recurring state-agency catalog sync
 * (migration 0034_phase24_catalog_sync.sql). All three are platform-level (no
 * tenant_id, no RLS): the sync runner + Paelode-admin review connect as
 * `migrator` (BYPASSRLS) to write; INSERT/UPDATE/DELETE are REVOKEd from
 * app_user, which keeps SELECT for the PLATFORM_ADMIN health endpoint.
 *
 *   - catalog_sync_config  — one row per state agency (OCC/RRC/WOGCC/NMOCD/NDIC);
 *                            schedule + last-run summary.
 *   - catalog_sync_runs    — per-run history log.
 *   - catalog_change_queue — detected changes pending Paelode-admin review
 *                            (never auto-applied — §3.2 / Task 24.9).
 *
 * Sources: docs/PLATFORM_CATALOG.md §3; master spec §50; gsd/DECISIONS_LOG.md
 * DEC-092. Depends on Task 24.1 (`wells`, `platform_operators`).
 */
import {
  boolean,
  check,
  index,
  integer,
  jsonb,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  unique,
  uuid,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { tenants, users } from './users.js';
import { appUserRole } from './enums.js';
import { platformOperators, wells } from './platform-catalog.js';

export const STATE_AGENCIES = ['OCC', 'RRC', 'WOGCC', 'NMOCD', 'NDIC'] as const;
export type StateAgency = (typeof STATE_AGENCIES)[number];

/** The agencies whose sync is built (RRC plan PR 4): the admin "Sync now" runs these. */
export const CATALOG_SYNC_BUILT_AGENCIES = ['OCC', 'RRC'] as const satisfies readonly StateAgency[];

/** 0198 — why a run FAILED (catalog_sync_runs.error_kind). */
export const CATALOG_SYNC_ERROR_KINDS = [
  'DOWNLOAD_FAILED',
  'SOURCE_PAGE_CHANGED',
  'PARSE_FAILED',
  'DB_FAILED',
] as const;
export type CatalogSyncErrorKind = (typeof CATALOG_SYNC_ERROR_KINDS)[number];

export const CATALOG_SYNC_STATUSES = ['SUCCESS', 'FAILED', 'PARTIAL'] as const;
export const CATALOG_RUN_STATUSES = ['RUNNING', 'SUCCESS', 'FAILED', 'PARTIAL'] as const;
export const CATALOG_CHANGE_TYPES = [
  'OPERATOR_ID_CHANGED',
  'WELL_STATUS_CHANGED',
  'NEW_OPERATOR',
  'WELL_DEACTIVATED',
  'OPERATOR_DEACTIVATED',
] as const;
export type CatalogChangeType = (typeof CATALOG_CHANGE_TYPES)[number];
export const CATALOG_REVIEW_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'DEFERRED'] as const;

// ── catalog_sync_config (no RLS; one row per agency) ─────────────────────────
export const catalogSyncConfig = pgTable(
  'catalog_sync_config',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    stateAgency: text('state_agency').notNull(),
    syncIntervalDays: integer('sync_interval_days').notNull().default(7),
    isActive: boolean('is_active').notNull().default(true),
    lastSyncAt: timestamp('last_sync_at', { withTimezone: true }),
    lastSyncStatus: text('last_sync_status'),
    lastSyncRecordsAdded: integer('last_sync_records_added').default(0),
    lastSyncChangesFlagged: integer('last_sync_changes_flagged').default(0),
    lastSyncError: text('last_sync_error'),
    nextSyncAt: timestamp('next_sync_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    unique('catalog_sync_config_state_agency_key').on(t.stateAgency),
    check(
      'catalog_sync_config_state_agency_check',
      sql`state_agency IN ('OCC','RRC','WOGCC','NMOCD','NDIC')`,
    ),
    check(
      'catalog_sync_config_last_status_check',
      sql`last_sync_status IS NULL OR last_sync_status IN ('SUCCESS','FAILED','PARTIAL')`,
    ),
  ],
);

export type CatalogSyncConfig = typeof catalogSyncConfig.$inferSelect;
export type NewCatalogSyncConfig = typeof catalogSyncConfig.$inferInsert;
export const catalogSyncConfigSelectSchema = createSelectSchema(catalogSyncConfig);
export const catalogSyncConfigInsertSchema = createInsertSchema(catalogSyncConfig);

// ── catalog_sync_runs (no RLS; history log) ──────────────────────────────────
export const catalogSyncRuns = pgTable(
  'catalog_sync_runs',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    stateAgency: text('state_agency').notNull(),
    startedAt: timestamp('started_at', { withTimezone: true }).notNull(),
    completedAt: timestamp('completed_at', { withTimezone: true }),
    status: text('status'),
    recordsProcessed: integer('records_processed').default(0),
    recordsAdded: integer('records_added').default(0),
    recordsUpdated: integer('records_updated').default(0),
    changesFlagged: integer('changes_flagged').default(0),
    errorMessage: text('error_message'),
    logGroup: text('log_group'),
    // 0198 — what failed, when a run FAILED (Corbin: show a failed download).
    errorKind: text('error_kind'),
    failedFile: text('failed_file'),
    sourceHost: text('source_host'),
    httpStatus: integer('http_status'),
    downloadAttempts: integer('download_attempts'),
    // One entry per source file: name, listing date and size, sha256, bytes, outcome, error, attempts.
    sourceFiles: jsonb('source_files'),
  },
  (t) => [
    index('catalog_sync_runs_agency_started_idx').on(t.stateAgency, t.startedAt.desc()),
    check(
      'catalog_sync_runs_status_check',
      sql`status IS NULL OR status IN ('RUNNING','SUCCESS','FAILED','PARTIAL')`,
    ),
    check(
      'catalog_sync_runs_error_kind_check',
      sql`error_kind IS NULL OR error_kind IN ('DOWNLOAD_FAILED','SOURCE_PAGE_CHANGED','PARSE_FAILED','DB_FAILED')`,
    ),
  ],
);

export type CatalogSyncRun = typeof catalogSyncRuns.$inferSelect;
export type NewCatalogSyncRun = typeof catalogSyncRuns.$inferInsert;
export const catalogSyncRunSelectSchema = createSelectSchema(catalogSyncRuns);
export const catalogSyncRunInsertSchema = createInsertSchema(catalogSyncRuns);

// ── catalog_change_queue (no RLS; pending Paelode-admin review) ───────────────
export const catalogChangeQueue = pgTable(
  'catalog_change_queue',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    stateAgency: text('state_agency').notNull(),
    changeType: text('change_type').notNull(),
    wellId: uuid('well_id').references(() => wells.id),
    operatorId: uuid('operator_id').references(() => platformOperators.id),
    oldValues: jsonb('old_values').notNull(),
    newValues: jsonb('new_values').notNull(),
    detectedAt: timestamp('detected_at', { withTimezone: true }).notNull().defaultNow(),
    syncRunId: uuid('sync_run_id').references(() => catalogSyncRuns.id),
    reviewStatus: text('review_status').notNull().default('PENDING'),
    reviewedBy: uuid('reviewed_by').references(() => users.id, { onDelete: 'set null' }),
    reviewedAt: timestamp('reviewed_at', { withTimezone: true }),
    reviewNotes: text('review_notes'),
    // Phase 24 / Task 24.9 (DEC-100) — Paelode approval stamp. notify_haulers flips
    // true on approve; the SECURITY DEFINER catalog_change_apply_approval fn fans out
    // PENDING hauler_catalog_change_reviews + BILLING_MANAGER notifications.
    paelodeApprovedAt: timestamp('paelode_approved_at', { withTimezone: true }),
    paelodeApprovedBy: uuid('paelode_approved_by').references(() => users.id, {
      onDelete: 'set null',
    }),
    paelodeReviewNotes: text('paelode_review_notes'),
    notifyHaulers: boolean('notify_haulers').notNull().default(false),
  },
  (t) => [
    index('catalog_change_queue_review_status_idx').on(t.reviewStatus),
    index('catalog_change_queue_agency_idx').on(t.stateAgency),
    index('catalog_change_queue_sync_run_idx').on(t.syncRunId),
    check(
      'catalog_change_queue_change_type_check',
      sql`change_type IN ('OPERATOR_ID_CHANGED','WELL_STATUS_CHANGED','NEW_OPERATOR','WELL_DEACTIVATED','OPERATOR_DEACTIVATED')`,
    ),
    check(
      'catalog_change_queue_review_status_check',
      sql`review_status IN ('PENDING','APPROVED','REJECTED','DEFERRED')`,
    ),
  ],
);

export type CatalogChangeQueueRow = typeof catalogChangeQueue.$inferSelect;
export type NewCatalogChangeQueueRow = typeof catalogChangeQueue.$inferInsert;
export const catalogChangeQueueSelectSchema = createSelectSchema(catalogChangeQueue);
export const catalogChangeQueueInsertSchema = createInsertSchema(catalogChangeQueue);

// ── hauler_catalog_change_reviews (tenant-scoped, RLS) ───────────────────────
// Phase 24 / Task 24.9 (DEC-100) — one row per (tenant, approved change) whose
// tank battery links the affected well. Created PENDING by the SECURITY DEFINER
// catalog_change_apply_approval fn on Paelode approve; the hauler then ACCEPTs
// (re-link operator), DISMISSes (keep config), or DEFERs.
export const HAULER_REVIEW_STATUSES = ['PENDING', 'ACCEPTED', 'DISMISSED', 'DEFERRED'] as const;
export type HaulerReviewStatus = (typeof HAULER_REVIEW_STATUSES)[number];

export const haulerCatalogChangeReviews = pgTable(
  'hauler_catalog_change_reviews',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    changeQueueId: uuid('change_queue_id')
      .notNull()
      .references(() => catalogChangeQueue.id),
    reviewStatus: text('review_status').notNull().default('PENDING'),
    reviewedBy: uuid('reviewed_by').references(() => users.id, { onDelete: 'set null' }),
    reviewedAt: timestamp('reviewed_at', { withTimezone: true }),
    notes: text('notes'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    unique('hauler_catalog_change_reviews_tenant_change_key').on(t.tenantId, t.changeQueueId),
    index('hauler_catalog_change_reviews_tenant_status_idx').on(t.tenantId, t.reviewStatus),
    index('hauler_catalog_change_reviews_change_idx').on(t.changeQueueId),
    check(
      'hauler_catalog_change_reviews_status_check',
      sql`review_status IN ('PENDING','ACCEPTED','DISMISSED','DEFERRED')`,
    ),
    pgPolicy('hauler_catalog_change_reviews_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
      withCheck: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type HaulerCatalogChangeReview = typeof haulerCatalogChangeReviews.$inferSelect;
export type NewHaulerCatalogChangeReview = typeof haulerCatalogChangeReviews.$inferInsert;
export const haulerCatalogChangeReviewSelectSchema = createSelectSchema(haulerCatalogChangeReviews);
export const haulerCatalogChangeReviewInsertSchema = createInsertSchema(haulerCatalogChangeReviews);
